import type {
  AnswersByKey,
  EmailStep,
  FlowVariant,
  FunnelOption,
  FunnelOptionFeedback,
  FunnelStep,
  JourneyFeatureStep,
  LoadingStep,
  PaymentPlanOption,
  PaymentStep,
  QuestionStep,
  ResultStep,
  StoryRecommendationStep,
  WelcomeStep
} from "./types";
import { config } from "../config";
import { GUARANTEE_NOTE } from "./guarantee";

// Runtime plans are supplied by the Stripe prices endpoint.
export const PARABLES_PLANS: PaymentPlanOption[] = [];

type FlowValue<T> = T | { a: T; b: T };

const split = <T,>(a: T, b: T): FlowValue<T> => ({ a, b });

const isSplitValue = <T,>(value: FlowValue<T>): value is { a: T; b: T } => {
  return typeof value === "object" && value !== null && "a" in value && "b" in value;
};

const resolveByFlow = <T,>(value: FlowValue<T>, flow: FlowVariant): T => {
  return isSplitValue(value) ? value[flow] : value;
};

type RawWelcomeStep = Omit<
  WelcomeStep,
  "title" | "description" | "ratingText" | "confirmLabel" | "heroSlides" | "featurePills"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  ratingText: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroSlides?: FlowValue<WelcomeStep["heroSlides"]>;
  featurePills?: FlowValue<WelcomeStep["featurePills"]>;
  finePrint?: FlowValue<string>;
};

type RawQuestionStep = Omit<
  QuestionStep,
  "title" | "subtitle" | "helperText" | "options" | "mediaLabel" | "mediaImage"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  helperText?: FlowValue<string>;
  options: FlowValue<FunnelOption[]>;
  mediaLabel?: FlowValue<string>;
  mediaImage?: FlowValue<string>;
  optionFeedback?: Record<string, FunnelOptionFeedback>;
};

type RawAffirmationStep = Omit<
  Extract<FunnelStep, { kind: "affirmation" }>,
  "title" | "description" | "confirmLabel" | "mediaLabel" | "heroImage"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  heroImage?: FlowValue<string>;
  mediaLabel?: FlowValue<string>;
};

type RawLoadingStep = Omit<LoadingStep, "title" | "subtitle" | "confirmLabel" | "loadingFact"> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  loadingFact: FlowValue<string>;
  heroImage?: FlowValue<string>;
};

type RawResultStep = Omit<
  ResultStep,
  "title" | "subtitle" | "personaName" | "description" | "questMap" | "confirmLabel"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  personaName: FlowValue<string>;
  description: FlowValue<string[]>;
  questMap: FlowValue<ResultStep["questMap"]>;
  confirmLabel: FlowValue<string>;
};

type RawJourneyFeatureStep = Omit<
  JourneyFeatureStep,
  "title" | "subtitle" | "description" | "confirmLabel"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description?: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
};

type RawEmailStep = Omit<
  EmailStep,
  "title" | "subtitle" | "description" | "confirmLabel" | "secondaryCta"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  secondaryCta?: FlowValue<string>;
};

type RawPaymentStep = Omit<
  PaymentStep,
  | "title"
  | "subtitle"
  | "description"
  | "benefits"
  | "plans"
  | "proofBullets"
  | "testimonials"
  | "confirmLabel"
  | "whyCardCopy"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  benefits: FlowValue<string[]>;
  plans: FlowValue<PaymentStep["plans"]>;
  proofBullets: FlowValue<string[]>;
  testimonials: FlowValue<PaymentStep["testimonials"]>;
  confirmLabel: FlowValue<string>;
  whyCardCopy: FlowValue<string>;
};

type RawStoryRecommendationStep = Omit<
  StoryRecommendationStep,
  "title" | "description" | "confirmLabel"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
};

type RawStep =
  | RawWelcomeStep
  | RawQuestionStep
  | RawAffirmationStep
  | RawLoadingStep
  | RawResultStep
  | RawJourneyFeatureStep
  | RawEmailStep
  | RawPaymentStep
  | RawStoryRecommendationStep;

const yesNoOptions: FunnelOption[] = [
  { id: "yes", label: "Yes", emoji: "✅" },
  { id: "no", label: "No", emoji: "❌" }
];

// ─── Persona system ──────────────────────────────────────────────────────────

type PersonaKey =
  | "spark_chaser"
  | "apprentice"
  | "ranger"
  | "navigator"
  | "sage"
  | "devoted_scholar";

type PersonaData = {
  heroImage: string;
  personaName: string;
  description: string[];
  questMap: ResultStep["questMap"];
};

type PersonaCopy = Omit<PersonaData, "questMap">;

const PERSONAS: Record<PersonaKey, PersonaCopy> = {
  spark_chaser: {
    heroImage: "/persona_shepard.webp",
    personaName: "The Shepherd",
    description: [
      "You don't need a routine. You need the right moment. When your heart is open, even a single verse can land deeper than a week of study. Trust that instinct."
    ]
  },
  apprentice: {
    heroImage: "/persona_disciple.webp",
    personaName: "The Disciple",
    description: [
      "You're the person who actually shows up. Day after day, no fanfare, no shortcuts. That kind of faithfulness is rare, and it's exactly how lasting understanding is built."
    ]
  },
  ranger: {
    heroImage: "/persona_wanderer.webp",
    personaName: "The Wanderer",
    description: [
      "You want to roam freely. Prophets one week, psalms the next, a letter from Paul after that. The Bible is vast and you intend to cover every corner of it."
    ]
  },
  navigator: {
    heroImage: "/persona_scribe.webp",
    personaName: "The Scribe",
    description: [
      "You want structure. A clear path, a logical progression, readings that connect and build toward real understanding. No randomness, just purpose."
    ]
  },
  sage: {
    heroImage: "/persona_prophet.webp",
    personaName: "The Prophet",
    description: [
      "Most people skim. You don't. You dig into context, trace ideas across books, and sit with the hard questions until they yield something true."
    ]
  },
  devoted_scholar: {
    heroImage: "/persona_berean.webp",
    personaName: "The Berean",
    description: [
      "You hold every word up to the light. You cross-check, you study, you refuse to accept easy answers. Your faith is rigorous because you believe it deserves to be."
    ]
  }
};

export const getPersonaHeroImages = (): string[] => {
  return Object.values(PERSONAS).map((persona) => persona.heroImage);
};

const PERSONA_ALIASES: Record<string, PersonaKey> = {
  spark_chaser: "spark_chaser",
  sparkchaser: "spark_chaser",
  spark: "spark_chaser",
  apprentice: "apprentice",
  ranger: "ranger",
  navigator: "navigator",
  sage: "sage",
  devoted_scholar: "devoted_scholar",
  devotedscholar: "devoted_scholar",
  scholar: "devoted_scholar"
};

const normalizePersonaAlias = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "_");
};

const resolvePersonaOverride = (value: string | null): PersonaKey | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizePersonaAlias(value);
  return PERSONA_ALIASES[normalized] ?? null;
};

const str = (answers: AnswersByKey, key: string): string =>
  typeof answers[key] === "string" ? answers[key] : "";

const arr = (answers: AnswersByKey, key: string): string[] =>
  Array.isArray(answers[key]) ? (answers[key] as string[]) : [];

const computeTraitMap = (answers: AnswersByKey): ResultStep["questMap"] => {
  // ── Curiosity ──────────────────────────────────────────────────────────
  const rabbitHoleCuriosity: Record<string, number> = {
    rarely: 0, sometimes: 1, often: 2, always: 3
  };
  const topicCount = Math.min(arr(answers, "topic_interest_grid").length, 12);
  const curiosityRaw =
    (rabbitHoleCuriosity[str(answers, "rabbit_hole")] ?? 1) +
    Math.round(topicCount / 3) +
    (str(answers, "rapid_fire_1") === "yes" ? 1 : 0) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0) +
    (arr(answers, "your_why").includes("curiosity") ? 1 : 0) +
    (arr(answers, "your_why").includes("understand_world") ? 1 : 0) +
    (arr(answers, "your_why").includes("conversation") ? 1 : 0);

  // ── Drive ──────────────────────────────────────────────────────────────
  const dailyTimeDrive: Record<string, number> = {
    "2_min": 0, "5_min": 1, "10_min": 3, pockets: 1
  };
  const questDiffDrive: Record<string, number> = {
    explorer: 0, adventurer: 1, scholar: 3
  };
  const scrollDrive: Record<string, number> = {
    under_30_min: 0, "30_to_60_min": 1, "1_to_2_hours": 2, "2_plus_hours": 3
  };
  const driveRaw =
    (dailyTimeDrive[str(answers, "daily_time")] ?? 1) +
    (questDiffDrive[str(answers, "quest_difficulty")] ?? 1) +
    (scrollDrive[str(answers, "scroll_habit_baseline")] ?? 1) +
    (arr(answers, "your_why").includes("invest") ? 1 : 0) +
    (arr(answers, "your_why").includes("replace_scroll") ? 1 : 0) +
    (arr(answers, "your_why").includes("games") ? 1 : 0);

  // ── Groundwork ─────────────────────────────────────────────────────────
  const kc1Scores: Record<string, number> = {
    yes_explain: 4, rough_idea: 3, caesar: 2, both: 2, no_idea: 0
  };
  const kc2Scores: Record<string, number> = {
    knew: 4, yes_explain: 4, heard: 3, gas: 3,
    seriously: 1, never_wondered: 1, fascinating: 0, no_idea: 0
  };
  const groundworkRaw =
    (kc1Scores[str(answers, "knowledge_check_1")] ?? 2) +
    (kc2Scores[str(answers, "knowledge_check_2")] ?? 2);

  // ── Focus ──────────────────────────────────────────────────────────────
  const wanderFocus: Record<string, number> = {
    wander: 0, mixed: 1, guided: 3
  };
  const perfFocus: Record<string, number> = {
    not_really: 0, little_bit: 2
  };
  const learningStyles = arr(answers, "learning_style");
  const focusRaw =
    (wanderFocus[str(answers, "wander_vs_guided")] ?? 1) +
    (perfFocus[str(answers, "perfectionism")] ?? 0) +
    (learningStyles.includes("revisit") ? 1 : 0) +
    (learningStyles.includes("visual") ? 1 : 0);

  const BASE = 2;
  const curiosity = curiosityRaw + BASE;
  const drive = driveRaw + BASE;
  const groundwork = groundworkRaw + BASE;
  const focus = focusRaw + BASE;
  const total = curiosity + drive + groundwork + focus;

  return [
    { id: "curiosity", label: "Curiosity", percent: Math.round((curiosity / total) * 100), icon: "🔍" },
    { id: "drive", label: "Devotion", percent: Math.round((drive / total) * 100), icon: "🔥" },
    { id: "groundwork", label: "Foundation", percent: Math.round((groundwork / total) * 100), icon: "📚" },
    { id: "focus", label: "Focus", percent: Math.round((focus / total) * 100), icon: "🧭" }
  ].sort((a, b) => b.percent - a.percent);
};

const resolvePersonaKey = (answers: AnswersByKey): PersonaKey => {
  // Depth score (0–5): rabbit_hole (0–3) + quest_difficulty (0–2)
  const rabbitHoleDepth: Record<string, number> = {
    rarely: 0, sometimes: 1, often: 2, always: 3
  };
  const questDifficultyDepth: Record<string, number> = {
    explorer: 0, adventurer: 1, scholar: 2
  };

  const rabbitHole = typeof answers.rabbit_hole === "string" ? answers.rabbit_hole : "";
  const questDifficulty = typeof answers.quest_difficulty === "string" ? answers.quest_difficulty : "";

  const depthScore =
    (rabbitHoleDepth[rabbitHole] ?? 0) +
    (questDifficultyDepth[questDifficulty] ?? 0);

  const depth: "casual" | "moderate" | "deep" =
    depthScore <= 1 ? "casual" : depthScore <= 3 ? "moderate" : "deep";

  // Style score (0–3): wander_vs_guided (0–2) + perfectionism (0–1)
  const wanderStyle: Record<string, number> = {
    wander: 0, mixed: 1, guided: 2
  };
  const perfectionismStyle: Record<string, number> = {
    not_really: 0, little_bit: 1
  };

  const wander = typeof answers.wander_vs_guided === "string" ? answers.wander_vs_guided : "";
  const perfectionism = typeof answers.perfectionism === "string" ? answers.perfectionism : "";

  const styleScore =
    (wanderStyle[wander] ?? 1) +
    (perfectionismStyle[perfectionism] ?? 0);

  const style: "free" | "structured" = styleScore <= 1 ? "free" : "structured";

  if (depth === "casual" && style === "free") return "spark_chaser";
  if (depth === "casual" && style === "structured") return "apprentice";
  if (depth === "moderate" && style === "free") return "ranger";
  if (depth === "moderate" && style === "structured") return "navigator";
  if (depth === "deep" && style === "free") return "sage";
  return "devoted_scholar";
};

export const getPersonaData = (answers: AnswersByKey): PersonaData => ({
  ...PERSONAS[resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers)
});

export const resolvePersonaOverrideFromUrl = (
  urlLike: URL | string = window.location.href
): PersonaKey | null => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const override =
    resolvePersonaOverride(url.searchParams.get("persona")) ??
    resolvePersonaOverride(url.searchParams.get("persona_override"));
  return override;
};

export const getPersonaDataWithOverride = (
  answers: AnswersByKey,
  overridePersona: PersonaKey | null
): PersonaData => ({
  ...PERSONAS[overridePersona ?? resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers)
});

// ─────────────────────────────────────────────────────────────────────────────

const createPostResultJourneySteps = (startOrder: number): RawJourneyFeatureStep[] => [
  {
    id: "story_1",
    order: startOrder + 1,
    kind: "journey_feature",
    layout: "story_pair",
    progress: 87,
    title: "Here are 2 stories picked for you",
    confirmLabel: "Next →"
  },
  {
    id: "chat_feature",
    order: startOrder + 2,
    kind: "journey_feature",
    layout: "chat_feature",
    progress: 89,
    title: "Chat with Moses.\nLearn from Jesus.\nSpeak to Solomon.",
    confirmLabel: "Continue"
  },
  {
    id: "daily_verse",
    order: startOrder + 3,
    kind: "journey_feature",
    layout: "daily_verse",
    progress: 90,
    title: "A daily verse on your homescreen",
    confirmLabel: "Continue"
  }
];

const FAITH_RECONNECT_RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: "/welcome-hero-poster.webp",
    heroVideo: { webm: "/welcome-hero.webm", mp4: "/welcome-hero.mp4" },
    title: split(
      "Watch Bible Stories<br />in the Spare Moments<br />of the Day",
      "Watch Bible Stories<br />in the Spare Moments<br />of the Day"
    ),
    description: split([], []),
    ratingText: "15,000+ · faithful readers",
    confirmLabel: "Continue"
  },
  {
    id: "age_group",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "age_group",
    title: "Who's starting the journey?",
    options: [
      { id: "18_35", label: "18-35", image: "/characters_v2_1.webp" },
      { id: "35_50", label: "35-50", image: "/characters_v2_2.webp" },
      { id: "50_65", label: "50-65", image: "/characters_v2_3.webp" },
      { id: "65_plus", label: "65+", image: "/characters_v2_4.webp" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "rabbit_hole",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "rabbit_hole",
    title: "Do you ever feel like your faith could be deeper if you really knew the Bible?",
    options: [
      { id: "rarely", label: "Not really", emoji: "😐" },
      { id: "sometimes", label: "Sometimes I wonder", emoji: "🙂" },
      { id: "often", label: "More than I'd like to admit", emoji: "😄" },
      { id: "always", label: "All the time - I know I'm missing so much", emoji: "🤩" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "affirmation_1",
    order: 4,
    kind: "affirmation",
    progress: 20,
    title: "You're not starting from scratch. You're reconnecting.",
    description: [
      "Parables meets you where you are and makes the Bible feel alive again, one beautifully told story at a time."
    ],
    mediaLabel: "Graphic placeholder",
    confirmLabel: "Show Me More →"
  },
  {
    id: "topic_interest_grid",
    order: 5,
    kind: "question",
    selection: "multi",
    answerKey: "topic_interest_grid",
    title: "What draws you to the Bible?",
    helperText: "Pick as many as you like",
    minSelections: 1,
    options: [
      { id: "stories_characters", label: "The stories and characters", emoji: "📖" },
      { id: "jesus_teachings", label: "The life and words of Jesus", emoji: "✝️" },
      { id: "wisdom_guidance", label: "Wisdom for everyday decisions", emoji: "🧠" },
      { id: "peace_anxiety", label: "Finding peace and calm", emoji: "🕊️" },
      { id: "purpose_direction", label: "Purpose and direction in life", emoji: "🧭" },
      { id: "relationships", label: "Being better in my relationships", emoji: "❤️" },
      { id: "hard_times", label: "Strength during hard times", emoji: "💪" },
      { id: "closer_to_god", label: "Growing closer to God", emoji: "🙏" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "scroll_habit_baseline",
    order: 6,
    kind: "question",
    selection: "single",
    answerKey: "scroll_habit_baseline",
    title: "How far have you gotten reading the Bible on your own?",
    subtitle: "Be honest - most people are in the same boat.",
    options: [
      { id: "under_30_min", label: "I've read a fair amount", emoji: "📖" },
      { id: "30_to_60_min", label: "Some parts, but not consistently", emoji: "🤔" },
      { id: "1_to_2_hours", label: "I've tried but never made it very far", emoji: "😅" },
      { id: "2_plus_hours", label: "Honestly, I've barely started", emoji: "🙈" }
    ],
    optionFeedback: {
      under_30_min: {
        text: "That's a great foundation. Parables will help you go deeper and connect the stories you already know.",
        variant: "positive"
      },
      "30_to_60_min": {
        text: "You're not alone. Most people have read bits and pieces. Parables makes it easy to fill in the gaps.",
        variant: "positive"
      },
      "1_to_2_hours": {
        text: "That's exactly why Parables exists. Short, beautifully told stories that finally make the Bible stick.",
        variant: "encouraging"
      },
      "2_plus_hours": {
        text: "You're in good company. Most people feel the same way. Parables gives you the easiest on-ramp to the Bible there is.",
        variant: "encouraging"
      }
    },
    confirmLabel: "Continue →"
  },
  {
    id: "daily_time",
    order: 7,
    kind: "question",
    selection: "single",
    answerKey: "daily_time",
    title: "How much time could you realistically spend each day with Parables?",
    options: [
      { id: "2_min", label: "2 minutes - quick and sharp", emoji: "⚡" },
      { id: "5_min", label: "5 minutes - a quiet moment", emoji: "🕯️" },
      { id: "10_min", label: "10 minutes - proper focus", emoji: "📖" },
      { id: "pockets", label: "Whenever I find a pocket of time", emoji: "🗓️" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "knowledge_check_1",
    order: 8,
    kind: "question",
    selection: "single",
    answerKey: "knowledge_check_1",
    mediaLabel: "Illustration: Moses and the burning bush",
    mediaImage: "/roman_republic.webp",
    title: "Do you know what job Moses had before God called him to lead Israel?",
    options: [
      { id: "yes_explain", label: "Yes, I could explain it", emoji: "✅" },
      { id: "rough_idea", label: "I have a rough idea", emoji: "🤔" },
      { id: "caesar", label: "Something about sheep?", emoji: "😅" },
      { id: "no_idea", label: "No idea honestly", emoji: "❌" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "knowledge_check_2",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "knowledge_check_2",
    mediaImage: "/roman_gladiators.webp",
    title: "Did you know David wrote most of the Psalms while running for his life?",
    options: [
      { id: "knew", label: "Yes, I knew that!", emoji: "🤩" },
      { id: "heard", label: "I kind of heard something like that", emoji: "🤔" },
      { id: "seriously", label: "Wait, seriously?", emoji: "😮" },
      { id: "fascinating", label: "No idea - but that's fascinating", emoji: "❌" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "learning_blocker",
    order: 11,
    kind: "question",
    selection: "single",
    answerKey: "learning_blocker",
    title: "What usually gets in the way of reading the Bible consistently?",
    options: [
      { id: "phone", label: "Too easy to pick up my phone instead", emoji: "📱" },
      { id: "time", label: "I genuinely don't feel like I have time", emoji: "⏰" },
      { id: "momentum", label: "Nothing out there has been good enough", emoji: "🔁" },
      { id: "grabbed", label: "It can feel hard to understand or connect with", emoji: "😶" },
      { id: "forget", label: "I forget what I've read", emoji: "🤔" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "affirmation_2",
    order: 12,
    kind: "affirmation",
    icon: "🏮",
    progress: 50,
    title: "Parables is already building your profile.",
    heroImage: "/parables_building.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    description: [
      "Based on your answers, we're building a personalised path to help you reconnect with the Bible, at your pace.",
      "<u>No guilt</u>. No pressure. Just your faith, growing daily 🙏."
    ],
    confirmLabel: "Keep Building →"
  },
  {
    id: "quest_difficulty",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "quest_difficulty",
    title: "How deep do you want to go?",
    options: [
      {
        id: "explorer",
        label: "Keep it simple",
        description: "Short stories, easy to follow, straight to the point",
        emoji: "🌱"
      },
      {
        id: "adventurer",
        label: "Give me the full picture",
        description: "Stories with context, reflections, and takeaways",
        emoji: "📖",
        badge: "Most Popular"
      },
      {
        id: "scholar",
        label: "I want real depth",
        description: "Rich detail, historical background, and connections across the Bible",
        emoji: "🎓"
      }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "rapid_fire_1",
    order: 15,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_1",
    mediaLabel: "Image: Moses and the Exodus",
    mediaImage: "/exodus.webp",
    title: "Are you interested in the story of Moses and the Exodus?",
    options: yesNoOptions,
    confirmLabel: "Continue →"
  },
  {
    id: "rapid_fire_2",
    order: 16,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_2",
    mediaLabel: "Image: David and Goliath",
    mediaImage: "/goliath.webp",
    title: "Are you interested in the story of David and Goliath?",
    options: yesNoOptions,
    confirmLabel: "Continue →"
  },
  {
    id: "your_why",
    order: 17,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    title: "Why do you want to reconnect with the Bible?",
    helperText: "Pick all that apply",
    minSelections: 1,
    options: [
      { id: "curiosity", label: "I want to feel closer to God again", emoji: "🙏" },
      { id: "conversation", label: "I need wisdom for a season I'm in", emoji: "🧠" },
      { id: "invest", label: "I want to raise my kids knowing these stories", emoji: "👨‍👩‍👧‍👦" },
      { id: "games", label: "I've been in church my whole life but don't really know the Bible", emoji: "⛪" },
      { id: "replace_scroll", label: "I keep trying but never make it past the first few books", emoji: "📖" },
      { id: "understand_world", label: "I feel called to go deeper in my faith", emoji: "❤️" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "generation_loading",
    order: 18,
    kind: "loading",
    progress: 85,
    title: "Crafting your personal journey...",
    subtitle: "Parables is building your personalised Bible journey.",
    heroImage: "/crafting_journey.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    loadingFact:
      "Adults who reconnect with the Bible through short daily stories report feeling closer to God within the first two weeks, even if they haven't opened a Bible in years.",
    modalQuestion: {
      answerKey: "perfectionism",
      title: "Quick one - are you a perfectionist?",
      options: [
        { id: "little_bit", label: "A little bit" },
        { id: "not_really", label: "Not really" }
      ]
    },
    confirmLabel: "See My Results →"
  },
  {
    id: "persona_result",
    order: 19,
    kind: "result",
    icon: "🏮",
    title: "Your personalised profile is ready.",
    heroImageMarginBottom: 10,
    personaName: "The Pilgrim",
    description: [
      "You're ready to reconnect with the Bible in a way that actually fits your life.",
      "Parables gives you the right path back in, and a way to grow closer to God, one story at a time."
    ],
    questMap: [
      { id: "curiosity", label: "Curiosity", percent: 30, icon: "🔍" },
      { id: "drive", label: "Devotion", percent: 28, icon: "🔥" },
      { id: "groundwork", label: "Foundation", percent: 22, icon: "📚" },
      { id: "focus", label: "Focus", percent: 20, icon: "🧭" }
    ],
    confirmLabel: "Save My Reading Path →"
  },
  ...createPostResultJourneySteps(20),
  {
    id: "email_capture",
    order: 25,
    kind: "email",
    answerKey: "email",
    title: "Save your profile",
    description: ["Your custom story profile will be ready after downloading the Parables app."],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Continue with Email →",
    trustNote: "No spam. Unsubscribe anytime.",
    quotes: [
      {
        text: "I've been in church for 30 years but honestly didn't know most of these stories. Parables changed that in weeks.",
        author: "Joseph T., 42",
        weeksUsingApp: 7,
        image: "/profile_tiny_joseph.webp"
      },
      {
        text: "It's so easy to read. I actually look forward to it every morning. Each story just pulls you in.",
        author: "Jess M., 36",
        weeksUsingApp: 12,
        image: "/profile_tiny_jess.webp"
      }
    ]
  },
  {
    id: "founding_member",
    order: 26,
    kind: "payment",
    icon: "✝️",
    answerKey: "selected_plan",
    title: "A faith habit that will stick.",
    subtitle: "Parables Premium",
    description: [
      "Illustrated Bible stories to read at your own pace",
      "Watch Bible stories come to life with videos in the app",
      "Chat to the bible and figures like **Moses**, **Jesus**, **Solomon**",
      "Build a simple habit with verses on your homescreen"
    ],
    plans: PARABLES_PLANS,
    benefits: [
      "Full access to Parables Premium",
      "Story videos in the app",
      "Your Reading Path and streak history saved"
    ],
    proofBullets: [
      "Personalised story paths that meet you exactly where you are",
      "Short daily stories that make the Bible finally click",
      "Progress tracking keeps your momentum visible and rewarding"
    ],
    testimonials: [
      {
        concern: "understanding",
        text: "I've been in church for 30 years but honestly didn't know most of these stories. Parables changed that in weeks.",
        author: "Joseph T., 42",
        weeksUsingApp: 7,
        image: "/profile_tiny_joseph.webp"
      },
      {
        concern: "ease",
        text: "It's so easy to read. I actually look forward to it every morning. Each story just pulls you in.",
        author: "Jess M., 36",
        weeksUsingApp: 12,
        image: "/profile_tiny_jess.webp"
      },
      {
        concern: "consistency",
        text: "I kept trying to read the Bible on my own and never made it past Genesis. Parables gave me the structure I needed to actually stick with it.",
        author: "Alan K., 47",
        weeksUsingApp: 19,
        image: "/profile_tiny_alan.webp"
      },
      {
        concern: "family",
        text: "My kids started asking me questions at dinner because I was suddenly full of amazing Bible stories to share. That alone was worth it.",
        author: "Luka O., 38",
        weeksUsingApp: 27,
        image: "/profile_tiny_luka.webp"
      },
      {
        concern: "understanding",
        text: "I raised my kids in the church but I always felt like I was faking it with the Bible. Parables let me quietly catch up. No shame, just growth.",
        author: "Jess N., 51",
        weeksUsingApp: 33,
        image: "/profile_tiny_jess.webp"
      }
    ],
    noChargeNote: GUARANTEE_NOTE,
    securityNotes: ["Secure checkout via Stripe", "Cancel anytime"],
    confirmLabel: "Continue to Secure Checkout →",
    whyCardCopy:
      "You don't need to start from scratch. You just need the right way back in. Parables helps you reconnect with the Bible through stories that finally make it real."
  },
  {
    id: "checkout_complete",
    order: 27,
    kind: "affirmation",
    icon: "✅",
    title: "Confirming your checkout and preparing your email update.",
    description: [
      "If this takes longer than a moment, tap Try Again to refresh your Stripe status.",
      "Thanks for supporting Parables."
    ],
    confirmLabel: "Done"
  }
];

const getTwelveWeekDateLabel = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 84);
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

const FAMILY_DEVOTIONAL_RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: "/family-hero.webp",
    title: split(
      '<span class="welcome-family-quote">"Mum, what happened to Noah?"</span><span class="welcome-family-eyebrow">For Christian parents who want to truly know the Bible, story by story.</span>',
      '<span class="welcome-family-quote">"Mum, what happened to Noah?"</span><span class="welcome-family-eyebrow">For Christian parents who want to truly know the Bible, story by story.</span>'
    ),
    description: split([], []),
    ratingText: "15,000+ · faithful readers and families",
    confirmLabel: "Continue"
  },
  {
    id: "parent_status",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "parent_status",
    title: "Are you a parent?",
    options: [
      { id: "yes", label: "Yes", emoji: "👨‍👩‍👧" },
      { id: "not_yet", label: "Not yet", emoji: "🌱" },
      { id: "grown", label: "My kids are grown", emoji: "🏡" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "age_group",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "age_group",
    title: "Who's starting the journey?",
    options: [
      { id: "18_35", label: "18-35", image: "/characters_v2_1.webp" },
      { id: "35_50", label: "35-50", image: "/characters_v2_2.webp" },
      { id: "50_65", label: "50-65", image: "/characters_v2_3.webp" },
      { id: "65_plus", label: "65+", image: "/characters_v2_4.webp" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "faith_home",
    order: 4,
    kind: "question",
    selection: "single",
    answerKey: "faith_home",
    title: "What does faith look like in your home right now?",
    options: [
      { id: "strong_more", label: "Strong, but I want more", emoji: "🙏" },
      { id: "church_surface", label: "We go to church, but it is surface-level", emoji: "⛪" },
      { id: "getting_back", label: "I am trying to get back", emoji: "🌤️" },
      { id: "distant", label: "It has been distant for a while", emoji: "🕯️" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "hands_bible_reflection",
    order: 5,
    kind: "affirmation",
    progress: 16,
    title: "That quiet pressure is more common than it feels.",
    description: [
      "Sometimes the hardest part is not believing. It is wondering why the stories feel so far away when your children ask."
    ],
    heroImage: "/hands_bible.webp",
    heroImageHasBorderRadius: true,
    confirmLabel: "Continue →"
  },
  {
    id: "child_story_confidence",
    order: 6,
    kind: "question",
    selection: "single",
    answerKey: "child_story_confidence",
    title: "When your child asks about a Bible story, how often can you actually explain it?",
    options: [
      { id: "explain_well", label: "I can explain it well", emoji: "📖" },
      { id: "gist_fuzzy", label: "I get the gist, but the details get fuzzy", emoji: "🧩" },
      { id: "fumble", label: "I usually fumble through it", emoji: "😅" },
      { id: "freeze", label: "I freeze up, and they can tell", emoji: "🧊" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "parents_not_alone",
    order: 7,
    kind: "affirmation",
    progress: 24,
    title: "Most Christian parents feel exactly like you do. They just do not say it out loud.",
    description: [
      '<ul class="affirmation-checklist"><li>7 in 10 say they could not confidently explain the Bible\'s core stories to their children.</li><li>You are not alone. You are not failing. You are just ready to start.</li></ul>'
    ],
    heroImage: "/parents_not_alone.webp",
    heroImageHasBorderRadius: true,
    confirmLabel: "I'm ready →"
  },
  {
    id: "church_belonging",
    order: 8,
    kind: "question",
    selection: "single",
    answerKey: "church_belonging",
    title: "At church, do you ever feel like everyone else gets it more than you do?",
    options: [
      { id: "never", label: "Never, I feel right at home", emoji: "🙏" },
      { id: "sometimes", label: "Sometimes, when sermons go deep", emoji: "🤷" },
      { id: "often", label: "More often than I would like to admit", emoji: "😶" },
      { id: "almost_always", label: "Almost always, like I am faking it", emoji: "💔" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "faith_conversation_avoidance",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "faith_conversation_avoidance",
    title: "Have you ever avoided a conversation about faith because you were worried you would be exposed?",
    options: [
      { id: "comfortable", label: "No, I am comfortable having them", emoji: "✋" },
      { id: "once_twice", label: "Once or twice", emoji: "🤐" },
      { id: "more_than_admit", label: "Yes, more than I would like to admit", emoji: "😬" },
      { id: "avoid_on_purpose", label: "Yes, I steer clear of them on purpose now", emoji: "😞" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "passing_faith_feeling",
    order: 10,
    kind: "question",
    selection: "single",
    answerKey: "passing_faith_feeling",
    title: "When you think about passing your faith to your kids, how do you feel?",
    options: [
      { id: "confident", label: "Confident. I have got this", emoji: "💪" },
      { id: "unsure_trying", label: "A bit unsure, but trying", emoji: "🌱" },
      { id: "scared_failing", label: "Honestly, scared I am already failing them", emoji: "😟" },
      { id: "avoid_thinking", label: "I try not to think about it", emoji: "🙈" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "cried_about_faith",
    order: 11,
    kind: "question",
    selection: "single",
    answerKey: "cried_about_faith",
    title: "Have you ever cried about your relationship with God or the Bible?",
    options: [
      { id: "never", label: "No, never", emoji: "🙂" },
      { id: "few_times", label: "A few times", emoji: "🥲" },
      { id: "more_than_admit", label: "More than I would like to admit", emoji: "💔" },
      { id: "recently", label: "Yes, recently", emoji: "🙏" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "noah_ark_confidence",
    order: 12,
    kind: "question",
    selection: "single",
    answerKey: "noah_ark_confidence",
    mediaImage: "/noah_ark_confidence.webp",
    title: "If your child asked, could you tell them why Noah built the ark?",
    options: [
      { id: "full_story", label: "Yes, I could tell the full story" },
      { id: "rough_idea", label: "I have a rough idea" },
      { id: "animals", label: "Something about animals two-by-two?" },
      { id: "no", label: "Honestly, no" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "moses_egypt_confidence",
    order: 13,
    kind: "question",
    selection: "single",
    answerKey: "moses_egypt_confidence",
    mediaImage: "/moses_egypt_confidence.webp",
    title: "Could you explain to your child why Moses left Egypt?",
    options: [
      { id: "walk_through", label: "Yes, I could walk them through it" },
      { id: "rough_idea", label: "I have a rough idea" },
      { id: "pharaoh_slaves", label: "Something about Pharaoh and slaves?" },
      { id: "struggle", label: "No, I would struggle" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "david_goliath_confidence",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "david_goliath_confidence",
    mediaImage: "/david_goliath_confidence.webp",
    title: "Could you tell your child the full story of David and Goliath?",
    options: [
      { id: "know_well", label: "Yes, I know it well" },
      { id: "basics", label: "The basics, but not the meaning behind it" },
      { id: "small_guy", label: "Small guy beats big guy with a slingshot?" },
      { id: "fumble", label: "I would fumble through it" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "jesus_death_confidence",
    order: 15,
    kind: "question",
    selection: "single",
    answerKey: "jesus_death_confidence",
    mediaImage: "/jesus_death_confidence.webp",
    title: "If your child asked, 'Why did Jesus have to die?' could you answer?",
    options: [
      { id: "confidence", label: "Yes, with confidence" },
      { id: "version", label: "I would give them a version of it" },
      { id: "sins_struggle", label: "Because of our sins, but I would struggle to explain why" },
      { id: "want_but_couldnt", label: "I would want to, but I could not" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "path_failure_affirmation",
    order: 16,
    kind: "affirmation",
    progress: 58,
    heroImage: "/path_failure_affirmation.webp",
    heroImageHasBorderRadius: true,
    title: "It is not that you do not believe. You just have not been taught.",
    description: [
      "7 in 10 Christian parents cannot confidently explain the Bible's core stories to their children. Not because their faith is weak, but because no one ever showed them how to learn."
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "path_failure_explanation",
    order: 17,
    kind: "affirmation",
    progress: 62,
    title: "That is not a faith failure.\nThat is a path failure.",
    description: [
      "The Bible is 66 books long. Most people open Genesis, get a few chapters into Leviticus, and quietly close it. That is not a faith failure. <strong>That is a path failure.</strong>",
      "<em>And paths can be fixed.</em>"
    ],
    confirmLabel: "Show me the path →"
  },
  {
    id: "tried_so_far",
    order: 18,
    kind: "question",
    selection: "multi",
    answerKey: "tried_so_far",
    title: "What have you tried so far?",
    helperText: "Pick all that apply, even the ones that did not stick.",
    minSelections: 1,
    options: [
      { id: "cover_to_cover", label: "Reading the Bible cover to cover", emoji: "📖" },
      { id: "study_group", label: "A Bible study or small group", emoji: "👥" },
      { id: "sermons_youtube", label: "Sermons or YouTube teachers", emoji: "📺" },
      { id: "bible_app", label: "A Bible app", emoji: "📱" },
      { id: "devotionals", label: "Daily devotionals", emoji: "📅" },
      { id: "kids_bibles", label: "Reading kids' Bibles with my children", emoji: "👶" },
      { id: "none", label: "None of these", emoji: "❌" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "tried_result",
    order: 19,
    kind: "question",
    selection: "single",
    answerKey: "tried_result",
    title: "And how did that go?",
    options: [
      { id: "overwhelmed", label: "I got overwhelmed and gave up", emoji: "😞" },
      { id: "inconsistent", label: "I could not stay consistent", emoji: "😴" },
      { id: "faded", label: "I started strong, then it faded", emoji: "⏳" },
      { id: "forgot", label: "I learned things, but could not remember them when it mattered", emoji: "🧠" },
      { id: "didnt_stick", label: "It did not really stick", emoji: "🤷" },
      { id: "worked", label: "Honestly, it worked well for me", emoji: "🌟" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "family_12_week_journey",
    order: 20,
    kind: "affirmation",
    progress: 70,
    title: "In 12 weeks, your whole home changes.",
    description: [
      "From the parent who fumbled to the parent your family looks to. From quiet shame to quiet strength. From watching faith from the outside, to leading it from within."
    ],
    heroImage: "/family_12_week_journey.webp",
    heroImageHasBorderRadius: true,
    confirmLabel: "Continue →"
  },
  {
    id: "quest_difficulty",
    order: 21,
    kind: "question",
    selection: "single",
    answerKey: "quest_difficulty",
    title: "How do you want the stories told?",
    options: [
      {
        id: "explorer",
        label: "Keep it simple",
        description: "Short, clear stories I can share with my family.",
        emoji: "🌱"
      },
      {
        id: "adventurer",
        label: "Give me the full picture",
        description: "Stories with context and meaning, so I really understand them.",
        emoji: "📖",
        badge: "Most Popular"
      },
      {
        id: "scholar",
        label: "I want to go deep",
        description: "History, connections, theological depth.",
        emoji: "🎓"
      }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "daily_time",
    order: 22,
    kind: "question",
    selection: "single",
    answerKey: "daily_time",
    title: "How much time can you give each day?",
    options: [
      { id: "2_min", label: "2 minutes, quick and meaningful", emoji: "⚡" },
      { id: "5_min", label: "5 minutes, a quiet daily moment", emoji: "☕" },
      { id: "10_min", label: "10 minutes, proper time with the Word", emoji: "📖" },
      { id: "pockets", label: "Whenever I find a pocket of time", emoji: "🌙" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "your_why",
    order: 23,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    title: "Why are you really here?",
    helperText: "Pick all that apply.",
    minSelections: 1,
    options: [
      { id: "curiosity", label: "I want to feel close to God again", emoji: "🙏" },
      { id: "invest", label: "I want to lead my family in faith", emoji: "👨‍👩‍👧" },
      { id: "games", label: "I want to stop feeling like a fraud", emoji: "😔" },
      { id: "conversation", label: "I want to answer my kids' questions with confidence", emoji: "💬" },
      { id: "understand_world", label: "I want to give my children what I never had", emoji: "🌱" },
      { id: "replace_scroll", label: "I want to finally know the Bible", emoji: "📖" }
    ],
    confirmLabel: "Continue →"
  },
  {
    id: "faith_path_ready",
    order: 24,
    kind: "affirmation",
    progress: 84,
    title: "Your custom journey",
    heroImage: "/journey.avif",
    heroImageHasBorderRadius: true,
    heroImageMarginBottom: -8,
    description: [
      `Based on your answers, by <strong>${getTwelveWeekDateLabel()}</strong>, you will have walked through:`,
      '<ul class="affirmation-checklist faith-path-checklist"><li>Creation, the Fall, and the foundations of faith</li><li>Noah, Abraham, and Moses, the great patriarchs</li><li>David, Solomon, and the Prophets, who they were and why they mattered</li><li>The full life of Jesus, from manger to cross to resurrection</li></ul>',
      '<span class="faith-path-closing">Next time your child asks, <strong>you\'ll have the answer.</strong></span>'
    ],
    confirmLabel: "Take me there →"
  },
  ...createPostResultJourneySteps(25),
  {
    id: "email_capture",
    order: 30,
    kind: "email",
    answerKey: "email",
    title: "Save your Faith Journey",
    description: ["Save your family reading profile and get the link to continue in the app. Build your daily habit together."],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Continue with Email →",
    trustNote: "No spam. Unsubscribe anytime.",
    quotes: [
      {
        text: "My daughter asked about Easter for the first time last month. I told her the full story, and I cried.",
        author: "Joseph T., 42",
        weeksUsingApp: 6,
        image: "/profile_tiny_joseph.webp"
      },
      {
        text: "Bedtime stories used to be Goodnight Moon. Now it is David and Goliath. My five-year-old has favourites.",
        author: "Jess M., 36",
        weeksUsingApp: 9,
        image: "/profile_tiny_jess.webp"
      }
    ]
  },
  {
    id: "founding_member",
    order: 31,
    kind: "payment",
    icon: "✝️",
    answerKey: "selected_plan",
    title: "A faith habit that will stick.",
    description: [
      "Illustrated Bible stories to read at your own pace",
      "Watch Bible stories come to life with videos in the app",
      "Chat to the bible and figures like **Moses**, **Jesus**, **Solomon**",
      "Build a simple habit with verses on your homescreen"
    ],
    plans: PARABLES_PLANS,
    benefits: [
      "Full access to Parables Premium",
      "Story videos in the app",
      "Your family's Faith Path and progress saved"
    ],
    proofBullets: [
      "Short Bible stories you can remember and share with your children",
      "A 12-week path through the stories every Christian parent wants to know",
      "Progress tracking that keeps momentum visible without adding pressure"
    ],
    testimonials: [
      {
        concern: "family",
        text: "My daughter asked about Easter for the first time last month. I told her the full story, and I cried. I felt like a real Christian for the first time in years.",
        author: "Joseph T., 42",
        weeksUsingApp: 6,
        image: "/profile_tiny_joseph.webp"
      },
      {
        concern: "family",
        text: "Bedtime stories used to be Goodnight Moon. Now it is David and Goliath. My five-year-old has favourites.",
        author: "Jess M., 36",
        weeksUsingApp: 9,
        image: "/profile_tiny_jess.webp"
      },
      {
        concern: "understanding",
        text: "I felt like a fraud at church for 20 years. After 8 weeks of Parables, I finally feel like I belong there.",
        author: "Alan K., 47",
        weeksUsingApp: 8,
        image: "/profile_tiny_alan.webp"
      }
    ],
    noChargeNote: GUARANTEE_NOTE,
    securityNotes: ["Secure checkout via Stripe", "Cancel anytime"],
    confirmLabel: "Continue to Secure Checkout →",
    whyCardCopy:
      "You do not need to pretend you know every story. You need a path that teaches you clearly, gently, and story by story, so you can pass faith on with confidence."
  },
  {
    id: "checkout_complete",
    order: 32,
    kind: "affirmation",
    icon: "✅",
    title: "Confirming your checkout and preparing your Faith Path.",
    description: [
      "If this takes longer than a moment, tap Try Again to refresh your Stripe status.",
      "Thanks for supporting Parables."
    ],
    confirmLabel: "Done"
  }
];

const resolveStepByFlow = (step: RawStep, flow: FlowVariant): FunnelStep => {
  switch (step.kind) {
    case "welcome":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        ratingText: resolveByFlow(step.ratingText, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        heroSlides: step.heroSlides ? (resolveByFlow(step.heroSlides, flow) || undefined) : undefined,
        featurePills: step.featurePills
          ? (resolveByFlow(step.featurePills, flow) || undefined)
          : undefined,
        finePrint: step.finePrint ? resolveByFlow(step.finePrint, flow) : undefined
      };
    case "question":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        helperText: step.helperText ? resolveByFlow(step.helperText, flow) : undefined,
        mediaLabel: step.mediaLabel ? resolveByFlow(step.mediaLabel, flow) : undefined,
        mediaImage: step.mediaImage ? (resolveByFlow(step.mediaImage, flow) || undefined) : undefined,
        options: resolveByFlow(step.options, flow),
        optionFeedback: step.optionFeedback
      };
    case "affirmation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        heroImage: step.heroImage ? (resolveByFlow(step.heroImage, flow) || undefined) : undefined,
        mediaLabel: step.mediaLabel ? resolveByFlow(step.mediaLabel, flow) : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
    case "loading":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        loadingFact: resolveByFlow(step.loadingFact, flow),
        heroImage: step.heroImage ? (resolveByFlow(step.heroImage, flow) || undefined) : undefined
      };
    case "result":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        personaName: resolveByFlow(step.personaName, flow),
        description: resolveByFlow(step.description, flow),
        questMap: resolveByFlow(step.questMap, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
    case "journey_feature":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        description: step.description ? resolveByFlow(step.description, flow) : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
    case "email": {
      const { secondaryCta: rawSecondaryCta, ...emailRest } = step;
      return {
        ...emailRest,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        ...(rawSecondaryCta != null
          ? { secondaryCta: resolveByFlow(rawSecondaryCta, flow) }
          : {})
      };
    }
    case "payment":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle ? resolveByFlow(step.subtitle, flow) : undefined,
        description: resolveByFlow(step.description, flow),
        benefits: resolveByFlow(step.benefits, flow),
        plans: resolveByFlow(step.plans, flow),
        proofBullets: resolveByFlow(step.proofBullets, flow),
        testimonials: resolveByFlow(step.testimonials, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        whyCardCopy: resolveByFlow(step.whyCardCopy, flow)
      };
    case "story_recommendation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow)
      };
  }
};

const getEnabledRawSteps = (rawSteps: RawStep[]): RawStep[] => {
  return rawSteps.filter((step) => {
    if (step.id === "email_capture") {
      return config.enableEmailCapture;
    }

    return true;
  });
};

export const getFaithReconnectSteps = (flow: FlowVariant): FunnelStep[] => {
  return getEnabledRawSteps(FAITH_RECONNECT_RAW_STEPS).map((step) => resolveStepByFlow(step, flow));
};

export const getFamilyDevotionalSteps = (flow: FlowVariant): FunnelStep[] => {
  return getEnabledRawSteps(FAMILY_DEVOTIONAL_RAW_STEPS).map((step) => resolveStepByFlow(step, flow));
};

export const getFlowSteps = (flow: FlowVariant): FunnelStep[] => {
  return getFaithReconnectSteps(flow);
};

export const TOTAL_STEPS = getEnabledRawSteps(FAITH_RECONNECT_RAW_STEPS).length;

export const FLOW_META: Record<FlowVariant, { name: string; slug: string }> = {
  a: { name: "Old Testament", slug: "old-testament" },
  b: { name: "New Testament", slug: "new-testament" }
};

export type FunnelSection = {
  label: string;
  /** inclusive 0-based start index */
  start: number;
  /** inclusive 0-based end index */
  end: number;
};

const BASE_FUNNEL_SECTIONS: FunnelSection[] = [
  { label: "Profile", start: 0, end: 6 },
  { label: "Personality", start: 7, end: 13 },
  { label: "Personalise", start: 14, end: 21 }
];

export const getFunnelSections = (steps: FunnelStep[]): FunnelSection[] => {
  const maxIndex = steps.length - 1;
  return BASE_FUNNEL_SECTIONS.map((section, index) => ({
    label: section.label,
    start: Math.min(section.start, maxIndex),
    end: index === BASE_FUNNEL_SECTIONS.length - 1 ? maxIndex : Math.min(section.end, maxIndex)
  })).filter((section) => section.start <= section.end);
};

export const computeSectionProgress = (currentIndex: number, sections: FunnelSection[]): number[] => {
  return sections.map(({ start, end }) => {
    const size = end - start + 1;
    const completed = Math.min(Math.max(currentIndex - start, 0), size);
    return completed / size;
  });
};
